import { useState, type ReactNode } from 'react';
import type { ApiError } from '../lib/api.js';
import type { SubscriptionStatus } from '../lib/api.js';
import { titleCase } from '../lib/format.js';

/** Small shared presentation pieces. */

export function Spinner(): ReactNode {
  return <span className="spinner" role="status" aria-label="Loading" />;
}

export function Loading({ label = 'Loading…' }: { label?: string }): ReactNode {
  return (
    <div className="center-screen">
      <div className="row muted">
        <Spinner />
        <span>{label}</span>
      </div>
    </div>
  );
}

/**
 * Error display.
 *
 * Shows the request id when there is one — it is the single most useful thing
 * a user can quote when something goes wrong, and it ties the screen to a
 * server log line.
 */
export function ErrorBanner({
  error,
  message,
}: {
  error?: ApiError | null;
  message?: string | null;
}): ReactNode {
  const text = message ?? error?.message;
  if (!text) return null;

  return (
    <div className="banner banner-error" role="alert">
      <div>{text}</div>
      {error?.details && error.details.length > 0 && (
        <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.1rem' }}>
          {error.details.map((detail, index) => (
            <li key={index}>
              {detail.path ? `${detail.path}: ` : ''}
              {detail.message}
            </li>
          ))}
        </ul>
      )}
      {error?.requestId && (
        <div style={{ marginTop: '0.35rem', opacity: 0.75 }}>
          Reference <code>{error.requestId}</code>
        </div>
      )}
    </div>
  );
}

export function EmptyState({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}): ReactNode {
  return (
    <div className="empty">
      <h2>{title}</h2>
      {children}
    </div>
  );
}

const STATUS_TONE: Readonly<Record<SubscriptionStatus, string>> = {
  active: 'badge-success',
  trialing: 'badge-success',
  past_due: 'badge-warning',
  incomplete: 'badge-warning',
  paused: 'badge',
  canceled: 'badge-danger',
};

const STATUS_LABEL: Readonly<Record<SubscriptionStatus, string>> = {
  active: 'Active',
  trialing: 'Trial',
  // Named for what the customer has to do, not for the provider's internal state.
  past_due: 'Payment overdue',
  incomplete: 'Awaiting payment',
  paused: 'Paused',
  canceled: 'Canceled',
};

export function StatusBadge({ status }: { status: SubscriptionStatus }): ReactNode {
  return <span className={`badge ${STATUS_TONE[status]}`}>{STATUS_LABEL[status]}</span>;
}

export function InvoiceStatusBadge({ status }: { status: string }): ReactNode {
  const tone =
    status === 'paid'
      ? 'badge-success'
      : status === 'open'
        ? 'badge-warning'
        : status === 'uncollectible'
          ? 'badge-danger'
          : '';
  return <span className={`badge ${tone}`}>{titleCase(status)}</span>;
}

export function Card({
  title,
  actions,
  children,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <section className="card">
      {(title ?? actions) && (
        <header className="card-head">
          {title && <h2>{title}</h2>}
          {actions && <div className="card-head-actions">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

/**
 * A link with a copy button.
 *
 * The text stays visible and selectable rather than hiding behind the button:
 * `navigator.clipboard` needs a secure context, so on plain HTTP the button
 * cannot work and selecting the text by hand is the only way through.
 */
export function CopyLink({ value, label }: { value: string; label?: string }): ReactNode {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Nothing to report: the value is on screen and can be selected.
    }
  }

  return (
    <div className="row" style={{ gap: '0.5rem', alignItems: 'center' }}>
      <code className="mono small" style={{ wordBreak: 'break-all', flex: 1 }}>
        {value}
      </code>
      <button type="button" className="button-ghost" onClick={() => void copy()}>
        {copied ? 'Copied' : (label ?? 'Copy')}
      </button>
    </div>
  );
}

/**
 * A usage meter.
 *
 * An unlimited entitlement shows no bar at all — a progress bar with no end is
 * misleading. Over-allowance usage turns the bar amber and states the overage
 * explicitly rather than letting the bar silently cap at 100%.
 */
export function UsageMeter({
  quantity,
  limit,
  unit,
}: {
  quantity: number;
  limit: number | null;
  unit: string;
}): ReactNode {
  if (limit === null) {
    return (
      <div className="small faint">
        Unlimited · {quantity.toLocaleString()} {unit}
      </div>
    );
  }
  const ratio = limit === 0 ? 1 : Math.min(1, quantity / limit);
  const over = quantity > limit;
  return (
    <div className="meter">
      <div
        className={`meter-fill${over ? ' over' : ''}`}
        style={{ width: `${String(Math.round(ratio * 100))}%` }}
      />
    </div>
  );
}
